import React, { useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Upload } from 'lucide-react';

interface ArchivosUploadAreaProps {
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  itemLabel?: string;
}

export const ArchivosUploadArea: React.FC<ArchivosUploadAreaProps> = ({
  onUpload,
  isUploading,
  itemLabel = 'archivo',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        onUpload(e.dataTransfer.files[0]);
      }
    },
    [onUpload]
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onUpload(e.target.files[0]);
    }
  };

  return (
    <Card
      className={`border-2 border-dashed transition-colors ${
        dragActive
          ? 'border-blue-400 bg-blue-50'
          : 'border-gray-300 hover:border-gray-400'
      }`}
    >
      <CardContent
        className="p-8 text-center"
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center gap-4">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center ${
              dragActive ? 'bg-blue-100' : 'bg-gray-100'
            }`}
          >
            <Upload
              className={`w-8 h-8 ${
                dragActive ? 'text-blue-600' : 'text-gray-600'
              }`}
            />
          </div>

          <div className="space-y-2">
            <h4 className="text-lg font-semibold text-gray-900">
              {dragActive ? `¡Suelta ${itemLabel === 'archivo' ? 'el archivo' : `la ${itemLabel}`} aquí!` : `Sube ${itemLabel === 'archivo' ? 'un archivo' : `una ${itemLabel}`}`}
            </h4>
            <p className="text-gray-600">
              {`Arrastra y suelta ${itemLabel === 'archivo' ? 'un archivo' : `una ${itemLabel}`} aquí, o `}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-blue-600 hover:text-blue-700 font-medium underline"
                disabled={isUploading}
              >
                seleccionar {itemLabel === 'archivo' ? 'archivo' : itemLabel}
              </button>
            </p>
            <p className="text-xs text-gray-500">PDF, JPG o PNG (máx. 10MB)</p>
          </div>

          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="mt-2"
          >
            {isUploading ? 'Subiendo...' : 'Seleccionar archivo'}
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileInput}
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png"
            disabled={isUploading}
          />
        </div>
      </CardContent>
    </Card>
  );
};
